import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ApplicationDevelopment = () => {
  // Hero Banner Section
  const HeroBannerSection = () => (
    <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920" 
          alt="Application Development" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Custom <span className="text-green-500">Application Development</span> Solutions
        </h1>
        <p className="text-white text-xl max-w-3xl mx-auto">
          Transform your ideas into powerful applications. From mobile apps to enterprise software, we build scalable, secure, and user-friendly solutions tailored to your business needs.
        </p>
      </div>
    </section>
  );

  // Overview Section
  const OverviewSection = () => (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl font-bold mb-6">
              Building Applications That Drive <span className="text-green-600">Business Growth</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              At Plexgate, we specialize in crafting custom applications that enhance productivity, streamline operations, and engage users. Our experienced development team uses cutting-edge technologies and agile methodologies to deliver solutions that exceed expectations.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Whether you need a mobile app, desktop application, or enterprise software, we have the expertise to bring your vision to life. We focus on creating intuitive user experiences, robust functionality, and scalable architectures that grow with your business.
            </p>
          </div>
          <div>
            <img 
              src="https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800&h=600&fit=crop" 
              alt="Application Development" 
              className="rounded-lg shadow-2xl w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );

  // Services Offered Section
  const ServicesSection = () => {
    const services = [
      {
        title: 'Mobile App Development',
        description: 'Native iOS and Android apps, cross-platform solutions using React Native and Flutter, and progressive web apps (PWAs).',
        icon: '📱'
      },
      {
        title: 'Web Application Development',
        description: 'Single-page applications (SPAs), enterprise web portals, SaaS platforms, and custom web applications built with modern frameworks.',
        icon: '🌐'
      },
      {
        title: 'Enterprise Software Solutions',
        description: 'Custom ERP systems, CRM platforms, business intelligence tools, and workflow automation solutions.',
        icon: '🏢'
      },
      {
        title: 'Desktop Applications',
        description: 'Cross-platform desktop apps using Electron, native Windows/Mac applications, and legacy system modernization.',
        icon: '💻'
      },
      {
        title: 'API Development & Integration',
        description: 'RESTful APIs, GraphQL services, third-party integrations, and microservices architecture.',
        icon: '🔌'
      },
      {
        title: 'Cloud-Native Applications',
        description: 'Serverless architectures, containerized applications, cloud migration, and multi-cloud solutions.',
        icon: '☁️'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-green-600">Application Development</span> Services
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive development solutions covering all platforms and technologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Technology Stack Section
  const TechnologySection = () => {
    const technologies = {
      'Mobile': ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Xamarin'],
      'Frontend': ['React', 'Angular', 'Vue.js', 'Next.js', 'TypeScript'],
      'Backend': ['Node.js', 'Python', 'Java', '.NET', 'PHP'],
      'Database': ['PostgreSQL', 'MongoDB', 'MySQL', 'Redis', 'Firebase'],
      'Cloud': ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes'],
      'Tools': ['Git', 'CI/CD', 'Jest', 'Selenium', 'Jira']
    };

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-green-600">Technology Stack</span>
            </h2>
            <p className="text-gray-600">We use industry-leading technologies to build robust applications</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(technologies).map(([category, techs], index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6 border-l-4 border-green-500">
                <h3 className="text-xl font-bold mb-4 text-gray-900">{category}</h3>
                <div className="flex flex-wrap gap-2">
                  {techs.map((tech, i) => (
                    <span key={i} className="bg-white px-3 py-1 rounded-full text-sm text-gray-700 border border-gray-200">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Development Process Section
  const ProcessSection = () => {
    const steps = [
      {
        number: '01',
        title: 'Discovery & Planning',
        description: 'We analyze your requirements, define project scope, create technical specifications, and develop a detailed roadmap.'
      },
      {
        number: '02',
        title: 'Design & Prototyping',
        description: 'Our designers create wireframes, mockups, and interactive prototypes to visualize the application before development.'
      },
      {
        number: '03',
        title: 'Development & Testing',
        description: 'Agile development sprints with continuous integration, automated testing, and regular client feedback sessions.'
      },
      {
        number: '04',
        title: 'Deployment & Support',
        description: 'Smooth deployment to production, comprehensive training, documentation, and ongoing maintenance and support.'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our Development <span className="text-green-600">Process</span>
            </h2>
            <p className="text-gray-600">A proven methodology that ensures quality and timely delivery</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all">
                  <div className="bg-blue-900 text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Why Choose Us Section
  const WhyChooseSection = () => {
    const benefits = [
      {
        title: 'Experienced Team',
        description: 'Our developers have years of experience across multiple platforms and industries.',
        icon: '👥'
      },
      {
        title: 'Agile Methodology',
        description: 'Flexible development approach with regular sprints and continuous client collaboration.',
        icon: '🔄'
      },
      {
        title: 'Quality Assurance',
        description: 'Rigorous testing procedures including unit, integration, and user acceptance testing.',
        icon: '✅'
      },
      {
        title: 'Scalable Solutions',
        description: 'Applications built to scale with your business growth and evolving needs.',
        icon: '📈'
      },
      {
        title: 'Security First',
        description: 'Industry-standard security practices, encryption, and compliance with regulations.',
        icon: '🔒'
      },
      {
        title: 'Ongoing Support',
        description: 'Post-launch maintenance, updates, and technical support to keep your app running smoothly.',
        icon: '🛠️'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Why Choose <span className="text-green-600">Plexgate</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="text-4xl">{benefit.icon}</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // CTA Section
  const CTASection = () => (
    <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920" 
          alt="Team" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Ready to Build Your <span className="text-green-500">Custom Application</span>?
        </h2>
        <p className="text-white text-xl mb-8 max-w-3xl mx-auto">
          Let's discuss your project requirements and create a solution that drives your business forward.
        </p>
        <div className="flex gap-4 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
          >
            Get Started →
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-3 rounded-full hover:bg-gray-100 transition-colors font-medium"
          >
            View All Services
          </Link>
        </div>
      </div>
    </section>
  );

  return (
    <div className="application-development-page">
      <HeroBannerSection />
      <OverviewSection />
      <ServicesSection />
      <TechnologySection />
      <ProcessSection />
      <WhyChooseSection />
      <FAQSection />
      <CTASection />
    </div>
  );
};

// FAQ Section - MOVED OUTSIDE main component
const FAQSection = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const faqs = [
    {
      question: 'What types of applications can you develop?',
      answer: 'We develop mobile apps (iOS/Android), web applications, desktop software, enterprise solutions, and cloud-native applications across various industries.'
    },
    {
      question: 'How long does it take to develop an application?',
      answer: 'Timeline varies based on complexity, features, and platform. A simple mobile app may take 2-3 months, while enterprise solutions can take 6-12 months. We provide detailed timelines during the planning phase.'
    },
    {
      question: 'Do you provide post-launch support?',
      answer: 'Yes! We offer comprehensive maintenance and support packages including bug fixes, updates, performance monitoring, and feature enhancements.'
    },
    {
      question: 'Can you modernize our existing legacy application?',
      answer: 'Absolutely! We specialize in legacy system modernization, including re-platforming, refactoring, and migration to modern architectures and cloud platforms.'
    },
    {
      question: 'What is your development methodology?',
      answer: 'We follow Agile methodologies with 2-week sprints, regular demos, continuous integration, and close collaboration with clients throughout the development process.'
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">
            Frequently Asked <span className="text-green-600">Questions</span>
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-semibold text-lg">{faq.question}</span>
                <span className="text-2xl text-gray-600">
                  {expandedFAQ === index ? '−' : '+'}
                </span>
              </button>
              {expandedFAQ === index && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ApplicationDevelopment;